import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { DealPipelineInvestmentsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { UPDATE_INVESTMENT_MUTATION } from '@/components/dueDiligence-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { User } from '@/types/user';
import { Controller, useForm } from 'react-hook-form';
import { DEAL_PIPELINE_INVESTMENTS_QUERY, LIST_INVESTMENT_ASSIGNEES_QUERY } from '../queries';

interface ChangeAssigneeProps {
  isOpen: boolean;
  currentRow: DealPipelineInvestmentsQuery['Investments']['data'][number] | null;
  onClose: () => void;
}

const ChangeAssignee = ({ isOpen, currentRow, onClose }: ChangeAssigneeProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { handleSubmit, setValue, control } = useForm<{ assigneeId: string }>({ defaultValues: { assigneeId: '' } });

  const [listAssignees, setListAssignees] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateInvestment, { loading: updateInvestmentLoading }] = useMutation(UPDATE_INVESTMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [DEAL_PIPELINE_INVESTMENTS_QUERY]
  });

  const { loading: listInvestmentAssigneesLoading } = useQuery<{ listInvestmentAssignees: Pick<User, 'id' | 'firstName' | 'lastName'>[] }>(
    LIST_INVESTMENT_ASSIGNEES_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: ({ listInvestmentAssignees }) => {
        setListAssignees(listInvestmentAssignees.map((el) => ({ label: `${el.firstName} ${el.lastName}`, value: el.id })));
      },
      onError: (error) => {
        console.log(error);
      }
    }
  );

  useEffect(() => {
    if (!currentRow) return;

    const { assignee } = currentRow;
    if (assignee) {
      setValue('assigneeId', assignee.id);
    }
  }, [currentRow]);

  const handleChangeAssignee = (data: { assigneeId: string }) => {
    setErrorMessage(null);

    const { assigneeId } = data;
    updateInvestment({
      variables: {
        data: {
          id: currentRow.id,
          assigneeId,
          nextClose: currentRow.nextClose,
          finalClose: currentRow.finalClose
        }
      },
      onCompleted: () => {
        onClose();
      },
      onError: (error) => {
        console.error(error);
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  if (!currentRow) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Task"
      confirmButton={{
        name: 'Assign',
        onClick: handleSubmit(handleChangeAssignee),
        disabled: updateInvestmentLoading
      }}
      showRequiredFields={!updateInvestmentLoading}
      showCancelButton={!updateInvestmentLoading}
      showCloseIcon={!updateInvestmentLoading}
      isUseOutsideClick={!updateInvestmentLoading}
    >
      {updateInvestmentLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalBody>
            <Controller
              name="assigneeId"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Assignee is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label="Select An Assignee Firm"
                  value={value || ''}
                  options={listAssignees}
                  setValue={onChange}
                  errorText={error?.message}
                  isRequiredField
                  loading={listInvestmentAssigneesLoading}
                  styleType={!listAssignees?.length && !listInvestmentAssigneesLoading ? 'disabled' : 'default'}
                  size="lg"
                />
              )}
            />
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default ChangeAssignee;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 26px;
`;
